<template>
  <div class="room_icons--container">
    <span class="room_icons--icon room_icons--icon-availability room_icons--icon-available tooltip-dock-top"
          :class="{'room_icons--icon-unavailable': !iconsAvailable.anyRoomType, 'icon-low-inventory': $store.getters.roomTypeTotalAvailable(roomAvailabilityData)}"
          :data-tooltip="availabilityTooltip"
          :aria-label="availabilityTooltip">
      <svg><use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#symbol_calendar_valid"></use></svg>
    </span>
    <span v-if="icons['non-smoking']" class="room_icons--icon room_icons--icon-non_smoking tooltip-dock-top"
      :class="{'room_icons--icon-unavailable': !iconsAvailable['non-smoking']}"
      :data-tooltip="(iconsAvailable['non-smoking']) ? 'Non-Smoking Available' : 'Non-Smoking Sold Out'"
      :aria-label="(iconsAvailable['non-smoking']) ? 'Non-Smoking Available' : 'Non-Smoking Sold Out'">
      <svg><use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#symbol_non_smoking"></use></svg>
    </span>
    <span v-if="icons.smoking" class="room_icons--icon room_icons--icon-smoking tooltip-dock-top"
      :class="{'room_icons--icon-unavailable': !iconsAvailable.smoking}"
      :data-tooltip="(iconsAvailable.smoking) ? 'Smoking Available' : 'Smoking Sold Out'"
      :aria-label="(iconsAvailable.smoking) ? 'Smoking Available' : 'Smoking Sold Out'">
      <svg><use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#symbol_smoking"></use></svg>
    </span>
    <a v-if="icons.ada" class="room_icons--icon room_icons--icon-ada tooltip-dock-top"
       :class="{'room_icons--icon-unavailable': !iconsAvailable.ada}"
       :data-tooltip="(iconsAvailable.ada) ? 'Click for ' + page.TITLE + ' Accessibility Options' : 'Accessibility Options Sold Out'"
       :aria-label="(iconsAvailable.ada) ? 'Click for ' + page.TITLE + ' Accessibility Options' : 'Accessibility Options Sold Out'"
       :title="(iconsAvailable.ada) ? 'Click for ' + page.TITLE + ' Accessibility Options' : 'Accessibility Options Sold Out'"
       href="#" @click.prevent="showAdaVariants">
      <svg>
        <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#symbol_accessible"></use>
      </svg>
    </a>
  </div>
</template>

<script lang="ts">
/*!
 * Copyright (C) 2022-2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 */
/*
 *		2022-08-16
 *			- Refactor filter-widget.js hotel room icon related functionality to <hotel-room-icons> component.
 *
 *		2024-06-10 (jhoward)
 *			- Implement Typescript in <hotel-room-icons>.
 *			- Refactor <hotel-room-icons> to use BEM CSS naming.
 *			- Implement low inventory notification in <hotel-room-icons>.
 *			- Make ADA variants clickable in <hotel-room-icons> for easier booking.
*/
import {defineComponent, PropType} from 'vue'
import {HotelPage, HotelRoomData, RoomAvailabilityData} from "./defs";
import {mapGetters} from "vuex";

export default defineComponent({
  name: 'hotelRoomIcons',
  props: {
    roomAvailabilityData: {
      type: Object as PropType<RoomAvailabilityData>,
      required: true
    },
    roomData: {
      type: Array as PropType<HotelRoomData[]>,
      required: true
    },
    page: {
      type: Object as PropType<HotelPage>,
      required: true
    }
  },
  data() {
    return {
      lowInventoryThresholdCount: 10,
      lowInventoryThresholdPercent: 0.25,
    };
  },
  computed: {
    ...mapGetters([
      'bookingLink',
      'roomTypeTotalAvailable'
    ]),
    icons() {
      let output = {
        'non-smoking': false,
        smoking: false,
        ada: false
      };
      this.roomData.forEach(roomTypeData => {
        if (roomTypeData.ROOM_SMOKING) {
          output.smoking = true;
        }
        if (roomTypeData.ROOM_NON_SMOKING) {
          output['non-smoking'] = true;
        }
        if (roomTypeData.ROOM_ADA) {
          output.ada = true;
        }
      });

      return output;
    },
    iconsAvailable() {
      let output = {
        anyRoomType: this.isAvailable,
        'non-smoking': false,
        smoking: false,
        ada: false
      };
      this.roomAvailabilityData.ROOM_TYPES.forEach(roomType => {
        if (roomType.ROOM_SMOKING) {
          output.smoking = true;
        }
        if (roomType.ROOM_NON_SMOKING) {
          output['non-smoking'] = true;
        }
        if (roomType.ROOM_ADA) {
          output.ada = true;
        }
      });

      return output;
    },
    isAvailable() {
      return this.$store.getters.roomTypeIsAvailable(this.roomAvailabilityData);
    },
    adaVariants(): HotelRoomData[] {
      return this.roomData.filter(roomTypeData => roomTypeData.ROOM_ADA_VARIANT.length);
    },
    availabilityTooltip(): string {
      return (!this.iconsAvailable.anyRoomType)
        ? 'Room Sold Out'
        : (this.isLowInventory)
          ? 'Not many rooms left'
          : 'Room Available';
    },
    isLowInventory(): boolean {
      const totalInventory = this.$store.getters.roomTypeTotalAvailable(this.roomAvailabilityData);
      return totalInventory < this.lowInventoryThresholdCount;
    },
  },
  methods: {
    showAdaVariants() {
      const dialogBody = (!this.adaVariants.length)
        ? 'No accessibility options exist for this room type.'
        : `<p>Here are the accessibility options for "${this.page.TITLE}":</p><ul>` + this.adaVariants.map(this.getAdaVariantListItemHtml).join('') + `</ul>`;

      window.showOverlayDialog({
        alertMode: true,
        confirmAccept: 'CLOSE',
        confirmTitle: this.page.TITLE + ' Accessibility Options',
        confirmBody: dialogBody
      });
    },

    getAdaVariantListItemHtml(roomTypeData: HotelRoomData): string {
      if (this.isPageRoomTypeAvailable(roomTypeData.PRT_ID)) {
        return `<li><a href="${this.$store.getters.bookingLink(this.roomAvailabilityData, roomTypeData.PRT_ID, '', this.page.ID, 'accessibility-dialog')}" title="Click to book ${this.page.TITLE} ${roomTypeData.ROOM_ADA_VARIANT}">${roomTypeData.ROOM_ADA_VARIANT}</a></li>`;
      }

      return `<li>${roomTypeData.ROOM_ADA_VARIANT} (Sold Out)</li>`;
    },

    isPageRoomTypeAvailable(prtId: number) {
      for (let i = 0, l = this.roomAvailabilityData.ROOM_TYPES.length; i < l; i++) {
        if (
          this.roomAvailabilityData.ROOM_TYPES[i].hasOwnProperty('PRT_ID')
          && this.roomAvailabilityData.ROOM_TYPES[i].PRT_ID === prtId
        ) {
          return true;
        }
      }

      return false;
    }
  }
})
</script>

<style>
.room_icons--container {
  padding		:	24px 0;
  position	:	relative;
  text-align	:	center;
  white-space	:	nowrap;
  z-index		:	10;
}

.page-links .item .room_icons--container {
  bottom		:	40px;
  height		:	0;
  padding		:	0;
  position	:	absolute;
  right		:	8px;
  text-align	:	right;
}

.room_icons--icon {
  background-color	:	#E4E4E4;
  background-color	:	rgba(228, 228, 228, 0.87);
  /* background-image	:	url(/library/images/room-icons/room-icons-40x40.png); */
  background-position	:	40px 40px;
  border-radius		:	50%;
  color				:	#006757;
  display				:	inline-block;
  height				:	64px;
  padding				:	12px;
  position			:	relative;
  width				:	64px;

  -webkit-transition	:	opacity 250ms ease-in-out 100ms, -webkit-transform 350ms ease-in;
  transition			:	opacity 250ms ease-in-out 100ms, transform 350ms ease-in;

  -webkit-transition-timing-function	:	var(--ease-in-out-quad), var(--ease-in-quad);
  transition-timing-function			:	var(--ease-in-out-quad), var(--ease-in-quad);
}

.page-links .item .room_icons--icon {
  background-image	:	url(/library/images/room-icons/room-icons-20x20.png);
  background-position	:	40px 40px;
  height				:	32px;
  padding				:	6px;
  width				:	32px;
}

.page-links .item .room_icons--icon.icon-disabled {
  background-position	:	40px 40px !important;
}

.page-links .item .room_icons--icon.room_icons--icon-non_smoking {
  background-position	:	-124px -4px;
}

.page-links .item .room_icons--icon.room_icons--icon-non_smoking.filter-match {
  background-position	:	-124px -84px;
}

.page-links .item .room_icons--icon.room_icons--icon-non_smoking.room_icons--icon-unavailable {
  background-position	:	-124px -44px;
}

.page-links .item .room_icons--icon.room_icons--icon-non_smoking.room_icons--icon-unavailable.filter-match {
  background-position	:	-124px -124px;
}

.page-links .item .room_icons--icon.room_icons--icon-smoking {
  background-position	:	-84px -4px;
}

.page-links .item .room_icons--icon.room_icons--icon-smoking.filter-match {
  background-position	:	-84px -84px;
}

.page-links .item .room_icons--icon.room_icons--icon-smoking.room_icons--icon-unavailable {
  background-position	:	-84px -44px;
}

.page-links .item .room_icons--icon.room_icons--icon-smoking.room_icons--icon-unavailable.filter-match {
  background-position	:	-84px -124px;
}

.page-links .item .room_icons--icon.room_icons--icon-ada {
  background-position	:	-164px -4px;
}

.page-links .item .room_icons--icon.room_icons--icon-ada.filter-match {
  background-position	:	-164px -84px;
}

.page-links .item .room_icons--icon.room_icons--icon-ada.room_icons--icon-unavailable {
  background-position	:	-164px -44px;
}

.page-links .item .room_icons--icon.room_icons--icon-ada.room_icons--icon-unavailable.filter-match {
  background-position	:	-164px -124px;
}

.page-links .item .room_icons--icon.room_icons--icon-availability {
  background-position	:	-4px -4px;
}

.page-links .item .room_icons--icon.room_icons--icon-availability.filter-match {
  background-position	:	-4px -84px;
}

.page-links .item .room_icons--icon.room_icons--icon-availability.room_icons--icon-unavailable {
  background-position	:	-44px -44px;
}

.page-links .item .room_icons--icon.room_icons--icon-availability.room_icons--icon-unavailable.filter-match {
  background-position	:	-44px -124px;
}

html .room_icons--icon.filter-match {
  background-color	:	#006757;
  color				:	#FFF;
}

.room_icons--icon ~ .room_icons--icon {
  margin-left	:	12px;
}

.page-links .item .room_icons--icon ~ .room_icons--icon {
  margin-left	:	4px;
}

.room_icons--icon.room_icons--icon-available {
  color	:	#006757;
}

.room_icons--icon.room_icons--icon-unavailable {
  background-color	:	#F7F7F7;
  background-color	:	rgba(247, 247, 247, 0.87);
  color				:	#92B5B0;
}

.room_icons--icon-available {
  color	:	#006757;
}

.room_icons--icon[data-tooltip]::after {
  white-space	:	nowrap;
}

svg {
  width: 100%;
  height: 100%;
}
</style>